import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../lib/apiClient";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1646182504823-a02b768e28b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
      <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
    </svg>
  );
}

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotSent, setForgotSent] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const destinationAfterSignIn = (userObj, registering = false) => {
    if (userObj?.role === "admin") return "/admin";
    const pendingReturn = localStorage.getItem("pending_auth_return");
    if (pendingReturn && pendingReturn.startsWith("/") && !pendingReturn.startsWith("//")) {
      localStorage.removeItem("pending_auth_return");
      return pendingReturn;
    }
    return registering ? "/family-profile" : "/";
  };

  // Handle the return from Google (Emergent-managed auth) — lands here as /auth#session_id=...
  useEffect(() => {
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) return;
    const sessionId = decodeURIComponent(match[1]);
    // Clear the fragment so a refresh doesn't re-run this
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    setGoogleLoading(true);
    api
      .post("/auth/google/session", { session_id: sessionId })
      .then(({ data }) => {
        login(data.token, data.user);
        toast.success("Signed in with Google!");
        navigate(destinationAfterSignIn(data.user));
      })
      .catch((err) => {
        toast.error(apiError(err.response?.data?.detail) || "Google sign-in failed");
        setGoogleLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setForgotSent(data.message);
        setLoading(false);
        return;
      }
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const { data } = await api.post(path, form);
      if (!data || !data.token) {
        throw new Error("no-token");
      }
      login(data.token, data.user);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      // Complete pending Love intent on the original collection after sign-in.
      navigate(destinationAfterSignIn(data.user, mode === "register"));
    } catch (err) {
      // Never fail silently: surface a clear, visible message for API, network and timeout errors.
      let msg;
      if (err?.response) {
        msg = apiError(err.response.data?.detail) ||
          (err.response.status === 401 ? "Invalid email or password" : "Something went wrong. Please try again.");
      } else if (err?.code === "ECONNABORTED") {
        msg = "The server took too long to respond. Please try again.";
      } else if (err?.message === "no-token") {
        msg = "Sign-in did not complete. Please try again.";
      } else {
        msg = "Couldn't reach the server. Check your connection and try again.";
      }
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={HERO} alt="Wrapped gift" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-ink/10 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading font-black text-4xl leading-tight">Everything your family needs, in one calm place.</h2>
          <p className="mt-3 text-white/80 max-w-md">Plan coordinated looks for your children, save your searches, and consult official size charts. Real partner collections arrive as merchants join.</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <button onClick={() => navigate("/")} className="mb-8"><Logo height="h-10" /></button>
          <h1 className="font-heading font-black text-3xl text-ink">{mode === "login" ? "Welcome back" : mode === "forgot" ? "Reset your password" : "Create your account"}</h1>
          <p className="text-ink-soft mt-2">{mode === "login" ? "Sign in to your SIBLINGO family account." : mode === "forgot" ? "Enter the email you signed up with and we'll send a one-time link (valid for 60 minutes)." : "Create your account, then add your family."}</p>
          {mode === "forgot" && forgotSent && <p className="mt-4 rounded-2xl border border-line bg-white p-3 text-sm text-ink" role="status" data-testid="auth-forgot-sent">{forgotSent}</p>}

          <form onSubmit={submit} className="mt-8 space-y-4">
            {error && (
              <div
                data-testid="auth-error"
                role="alert"
                className="flex items-start gap-2 rounded-2xl border-2 border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
              >
                <span className="mt-0.5">⚠</span>
                <span>{error}</span>
              </div>
            )}
            {mode === "register" && (
              <div>
                <label htmlFor="auth-name" className="text-sm font-bold text-ink">Your name</label>
                <input
                  id="auth-name"
                  name="name"
                  autoComplete="name"
                  data-testid="auth-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 w-full h-14 px-5 rounded-2xl border-2 border-line bg-white focus:border-brand outline-none transition-all"
                  placeholder="Sarah"
                />
              </div>
            )}
            <div>
              <label htmlFor="auth-email" className="text-sm font-bold text-ink">Email</label>
              <input
                id="auth-email"
                name="email"
                autoComplete="email"
                data-testid="auth-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 w-full h-14 px-5 rounded-2xl border-2 border-line bg-white focus:border-brand outline-none transition-all"
                placeholder="you@email.com"
              />
            </div>
            {mode !== "forgot" && <div>
              <label htmlFor="auth-password" className="text-sm font-bold text-ink">Password</label>
              <input
                id="auth-password"
                name="password"
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                data-testid="auth-password-input"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 w-full h-14 px-5 rounded-2xl border-2 border-line bg-white focus:border-brand outline-none transition-all"
                placeholder="••••••••"
              />
              {mode === "login" && <button type="button" onClick={() => { setMode("forgot"); setError(""); setForgotSent(""); }} data-testid="auth-forgot-btn" className="mt-2 text-sm font-bold text-brand min-h-[44px] inline-flex items-center">Forgot password?</button>}
            </div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full h-14 rounded-full bg-brand text-white font-bold text-lg hover:bg-brand-hover active:scale-95 transition-all shadow-md disabled:opacity-60"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign In" : mode === "forgot" ? "Send reset link" : "Create Account"}
            </button>
          </form>

          {mode !== "forgot" && <><div className="flex items-center gap-3 my-5" data-testid="auth-or-divider">
            <span className="h-px flex-1 bg-line" />
            <span className="text-xs font-bold uppercase tracking-wider text-ink-muted">or</span>
            <span className="h-px flex-1 bg-line" />
          </div>

          <button
            type="button"
            onClick={googleSignIn}
            disabled={googleLoading}
            data-testid="auth-google-btn"
            className="w-full h-14 rounded-full bg-white border-2 border-line text-ink font-bold text-base flex items-center justify-center gap-3 hover:border-brand/40 hover:bg-blush/40 active:scale-95 transition-all disabled:opacity-60"
          >
            <GoogleIcon />
            {googleLoading ? "Signing you in…" : mode === "login" ? "Sign in with Google" : "Sign up with Google"}
          </button></>}

          <p className="text-center text-ink-soft mt-6">
            {mode === "login" ? "New to SIBLINGO?" : mode === "forgot" ? "Remembered it?" : "Already have an account?"}{" "}
            <button
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setForgotSent(""); }}
              data-testid="auth-toggle-btn"
              className="text-brand font-bold"
            >
              {mode === "login" ? "Create account" : "Sign in"}
            </button>
          </p>

          <div data-testid="auth-legal-links" className="mt-10 pt-6 border-t border-line flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-ink-muted">
            <button onClick={() => navigate("/about")} className="hover:text-brand">About</button>
            <button onClick={() => navigate("/contact")} className="hover:text-brand">Contact</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-brand">Privacy</button>
            <button onClick={() => navigate("/terms")} className="hover:text-brand">Terms</button>
            <button onClick={() => navigate("/affiliate-disclosure")} className="hover:text-brand">Affiliate Disclosure</button>
          </div>
        </div>
      </div>
    </div>
  );
}
